import {ResearchLitElement, html, css} from '@core/research-lit-element';
import './research-filter-scroll';
import '@bog-design-system/bd-accordion';
import '@bog-design-system/bd-font';
import {ResearchFilterItem} from './research-filter-item';

/**
 * `research-filter`
 * filter
 *
 * @customElement
 * @polymer
 * @design https://www.figma.com/file/IIjl4hyfYtsYzuatJGf5cX/Filter-Component?node-id=1%3A216&t=N0VZTXcaxbiL1Bwt-0
 */
export class ResearchFilter extends ResearchLitElement {
    static get is() {
        return 'research-filter';
    }

    static get styles() {
        // language=CSS
        return css`
            :host {
                display: block;
                --max-height: 352px;
                --min-width: 300px;
                --border-color: var(--color-invert-component-tr-60);
                --border-radius: var(--border-radius-32);
                --bg-color: var(--color-invert-component-tr-90);
                --bg-hover-color: var(--color-invert-component-tr-90);
                ---header-bg-active: var(--color-invert-component-tr-90);
                ---accordion-c-bg: var(--color-invert-component-tr-90);
                ---padding-bottom: var(--space-16);
                --additional-filter-h: 48px;
                --additional-filter-padding: 0px var(--space-16);
                --additional-filter-justify: flex-start;
                --filter-header-color: var(--color-invert-component-tr-20);
            }

            .wrapper {
                max-height: var(--max-height);
                min-width: var(--min-width);
                background-color: var(--bg-color);
                border-radius: var(--border-radius);
                border: 1px solid var(--border-color);
                display: block;
                overflow: hidden;
                transition: max-height 0.2s;
                padding-bottom: 0px;
            }

            :host([active]) .wrapper {
                padding-bottom: var(---padding-bottom);
            }

            .filter-accordion {
                max-height: var(--max-height);
                min-width: var(--min-width);
                --header-border-radius: var(--border-radius);
                --accordion-h-bg: var(--color-invert-component-tr-90);
                --header-bg-hover: var(--bg-hover-color);
                --accordion-h-border: transparent;
                --content-border-radius: 0 0 var(--border-radius) var(--border-radius);
                --header-min-h: 48px;
                --header-padding: 0px;
                border-radius: var(--border-radius);
                --accordion-expanded-h-border: transparent;
                --accordion-expanded-h-border-left: transparent;
                --header-bg-active: var(---header-bg-active);
                --accordion-c-bg: var(---accordion-c-bg);
                --content-border-color: none;
            }

            .filter-header {
                color: var(--filter-header-color);
            }

            research-filter-scroll {
                max-height: calc(var(--max-height) - var(--header-min-h));
            }

            :host([show-additional-filter]) research-filter-scroll {
                max-height: calc(var(--max-height) - var(--header-min-h) - var(--additional-filter-h));
            }

            .additional-filter {
                height: var(--additional-filter-h);
                align-items: center;
                justify-content: var(--additional-filter-justify);
                padding: var(--additional-filter-padding);
            }

            .additional-filter ::slotted(*) {
                width: 100%;
            }
        `;
    }

    render() {
        // language=HTML
        return html`
            <div class="wrapper">
                <bd-accordion
                    class="filter-accordion"
                    ?expanded="${this.active}"
                    ?active="${this.active}"
                    @accordion-closed="${this._closed}"
                    @accordion-opened="${this._opened}"
                >
                    <bd-font slot="header-center" type="number" class="filter-header"> ${this.title} </bd-font>

                    <div class="content-wrapper" slot="content">
                        ${this.showAdditionalFilter ?
                            html`
                                  <div class="additional-filter">
                                      <slot name="additional-filter"></slot>
                                  </div>
                              ` :
                            ''}

                        <research-filter-scroll>
                            <slot name="filter-item" id="filterItem" @slotchange="${this._onFiltersChanged}"></slot>
                        </research-filter-scroll>
                    </div>
                </bd-accordion>
            </div>
        `;
    }

    static get properties() {
        return {
            /**
             * @property {string} title
             * filter header title
             */
            title: {
                type: String,
            },
            /**
             * @property {string} type
             * multiple or single selector
             */
            type: {
                type: String,
                reflect: true,
                attribute: 'type',
            },
            /**
             * @property {boolean} active
             * active property defines filter-container is opened or closed
             */
            active: {
                type: Boolean,
                reflect: true,
            },
            /**
             * @property {boolean} showAdditionalFilter
             * showAdditionalFilter property defines if search input or range selector is shown
             */
            showAdditionalFilter: {
                type: Boolean,
                reflect: true,
                attribute: 'show-additional-filter',
            },
            _selected: {
                type: Array,
            },
        };
    }

    constructor() {
        super();
        this.type = this.validTypes[0];
        this._selected = [];
    }

    _opened() {
        this.active = true;
    }

    _closed() {
        this.active = false;
    }

    _onFiltersChanged(event) {
        const nodes = event.target.assignedNodes();
        for (const node of nodes) {
            if (node instanceof ResearchFilterItem) {
                node.type = this.type;
                if (this.type === this.validTypes[1]) {
                    node.addEventListener('filter-check-changed', this._selectFilter.bind(this));
                    node.addEventListener('sub-filter-check-changed', this._changedFilterItemChecked.bind(this));
                } else {
                    node.addEventListener('filter-check-changed', this._filterChanged.bind(this));
                }
            }
        }
    }

    _selectFilter(e) {
        if (e.target && e.target.slot === 'filter-item') {
            this._changedFilterItemChecked(e);
        }
    }

    _changedFilterItemChecked(e) {
        const filterItems = this.shadowRoot.querySelector('#filterItem').assignedNodes();
        for (const item of filterItems) {
            if (item instanceof ResearchFilterItem) {
                if (item !== e.target) {
                    item.checked = false;
                    item._uncheckeSubItems();
                } else {
                    item.checked = true;
                }
            }
        }
    }

    _filterChanged(e) {
        let dispatchEvent = true;
        const item = e.target;
        if (!this._selected) {
            this._selected = [];
        }
        if (item && item.slot === 'filter-item') {
            if (e.detail.checked) {
                if (!(this._selected && this._selected.includes(item.value))) {
                    this._selected = [...this._selected, item.value];
                    item._checkAllSubItems();
                } else {
                    dispatchEvent = false;
                }
            } else {
                this._selected = this._selected.filter((cat) => cat !== item.value);
                item._uncheckeSubItems();
            }

            if (dispatchEvent) {
                this.dispatchEvent(
                    new CustomEvent('filter-changed', {
                        detail: {
                            selected: this._selected,
                            checked: item.checked,
                        },
                        bubbles: true,
                    })
                );
            }
        }
    }

    get validTypes() {
        return ['multiple', 'single'];
    }
}
window.customElements.define(ResearchFilter.is, ResearchFilter);
